import { useFormatter } from '@api/useFormatter'
import { addCommasToNumber, Horizontal, Texto, useThemeContext, Vertical } from '@gravitate-js/excalibrr'
import { ReadableDifference } from '@utils/general'
import { Spin } from 'antd'
import moment from 'moment-timezone'
import { useCallback, useMemo } from 'react'
import { Area, AreaChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

export function StoreDetailsTankRundown({ tankRundownData, rundownToggled, graphMarkers, timezone, isMobile = false }) {
  const { isDarkTheme } = useThemeContext()
  const color1 = isDarkTheme ? 'var(--theme-option)' : 'var(--theme-color-1)'
  const chartData = useMemo(() => {
    if (!tankRundownData) return []

    const actuals = tankRundownData?.data?.find((d) => d?.id === 'actuals')
    const forecasts = tankRundownData?.data?.find((d) => d?.id === 'forecast')
    const forecastsWithDrops = tankRundownData?.data?.find((d) => d?.id === 'forecast_with_drops')

    // Create a map to merge all data by date
    const dataByDate = new Map()

    // Add actuals data
    actuals?.data.forEach((d) => {
      dataByDate.set(d.x, {
        date: d.x,
        actual: d.y,
        orders: d.orders,
      })
    })

    // Add forecast data
    forecasts?.data.forEach((d) => {
      const existing = dataByDate.get(d.x) || { date: d.x }
      dataByDate.set(d.x, {
        ...existing,
        forecast: d.y,
      })
    })

    // Add forecast with drops data
    forecastsWithDrops?.data.forEach((d) => {
      const existing = dataByDate.get(d.x) || { date: d.x }
      dataByDate.set(d.x, {
        ...existing,
        forecastDrops: d.y,
        orders: d.orders, // Override orders with forecast drops orders
      })
    })

    // Convert map to sorted array
    return Array.from(dataByDate.values()).sort((a, b) => new Date(a.date) - new Date(b.date))
  }, [tankRundownData])

  const maxPhysicalVolume = useMemo(
    () => tankRundownData?.markers?.find((d) => d.name === 'Max Physical Volume')?.value,
    [tankRundownData]
  )

  const visibleMarkers = useMemo(
    () => tankRundownData?.markers?.filter((m) => graphMarkers[m.name]),
    [tankRundownData, graphMarkers]
  )

  const groupedMarkers = useMemo(
    () =>
      visibleMarkers?.reduce(
        (acc, m) => ({ ...acc, [m.value]: visibleMarkers?.filter((vm) => vm.value === m.value) }),
        {}
      ),
    [visibleMarkers]
  )

  const { formatVolume } = useFormatter()
  const formattedUnit = formatVolume(null, false)

  const kFormatter = useCallback((num) => {
    return Math.abs(num) > 999
      ? Math.sign(num) * (Math.abs(num) / 1000).toFixed(1) + 'K'
      : Math.sign(num) * Math.abs(num)
  }, [])

  return (
    <Vertical height='100%' style={{ maxHeight: '100%' }}>
      <div className='bg-1'>
        <Texto className='p-3 pb-0'>Store Times are reflected in {timezone}</Texto>
      </div>

      <div className='flex-1' style={{ height: '100%' }}>
        {tankRundownData?.markers ? (
          <ResponsiveContainer width='100%' height='100%'>
            <AreaChart width={500} height={500} data={chartData}>
              <defs>
                <linearGradient id='forecastDropsFill' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='var(--theme-color-2)' stopOpacity={0.75} />
                  <stop offset='95%' stopColor='var(--theme-color-2)' stopOpacity={0.1} />
                </linearGradient>
                <linearGradient id='forecastFill' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='var(--theme-optimal)' stopOpacity={0.75} />
                  <stop offset='95%' stopColor='var(--theme-optimal)' stopOpacity={0.1} />
                </linearGradient>
                <linearGradient id='actualFill' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor={color1} stopOpacity={0.75} />
                  <stop offset='95%' stopColor={color1} stopOpacity={0.1} />
                </linearGradient>
              </defs>
              // Axis
              <YAxis
                tickFormatter={kFormatter}
                domain={[0, tankRundownData?.y_max]}
                type='number'
                tickCount='12'
                label={
                  isMobile
                    ? {}
                    : {
                        value: formattedUnit,
                        angle: -90,
                        position: 'insideLeft',
                        offset: 10,
                        stroke: 'var(--theme-option)',
                      }
                }
                width={isMobile ? 40 : 60}
              />
              <XAxis
                dataKey='date'
                scale='band'
                tickFormatter={(date) => moment.utc(date).format('M/D hA')}
                angle={10}
              />
              /* Content */
              {rundownToggled && (
                <Area
                  type='monotone'
                  dataKey='forecastDrops'
                  fillOpacity={0.5}
                  fill='url(#forecastDropsFill)'
                  stroke='var(--theme-color-2)'
                  strokeWidth={2}
                  legendType='circle'
                  name='Forecast Drops'
                />
              )}
              <Area
                type='monotone'
                dataKey='forecast'
                fillOpacity={0.5}
                fill='url(#forecastFill)'
                stroke='var(--theme-optimal)'
                strokeWidth={2}
                legendType='circle'
                name='Forecast'
              />
              <Area
                type='monotone'
                dataKey='actual'
                fillOpacity={0.5}
                fill='url(#actualFill)'
                stroke={color1}
                strokeWidth={2}
                legendType='circle'
                name='Actual'
              />
              {Object.entries(groupedMarkers).map(([yValue, markers]) => {
                return (
                  <ReferenceLine
                    key={yValue}
                    y={yValue}
                    label={<ReferenceLabel value={markers.map((m) => m.name).join(', ')} fill='var(--theme-success)' />}
                    stroke='var(--theme-success)'
                    strokeWidth={2}
                  />
                )
              })}
              <Tooltip
                content={
                  <LoadedTooltip
                    maxPhysicalVolume={maxPhysicalVolume}
                    rundownToggled={rundownToggled}
                    timezone={timezone}
                  />
                }
              />
              <Legend verticalAlign='top' height={36} layout='horizontal' />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <Vertical horizontalCenter verticalCenter>
            <Spin />
          </Vertical>
        )}
      </div>
    </Vertical>
  )
}

function ReferenceLabel({ fill, value, fontSize, viewBox, dy, dx }) {
  const y = viewBox.y + 3
  const charWidth = 6
  const rectWidth = value?.length * charWidth

  return (
    <g>
      <rect
        x='80%'
        dx={dx}
        y={y - 9}
        width={rectWidth}
        height='12'
        fill='white'
        transform={`translate(-${rectWidth / 2}, 0)`}
      />
      <text x='80%' y={y} dy={dy} dx={dx} fill={fill} fontSize={fontSize || 10} fontWeight='bold' textAnchor='middle'>
        {value}
      </text>
    </g>
  )
}

function LoadedTooltip({ active, payload, maxPhysicalVolume, rundownToggled, timezone }) {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload

    const date = moment.utc(data.date)
    const dateTime = date.format('ddd D LT')

    const volume = data.actual ? data.actual : rundownToggled ? data.forecastDrops : data.forecast

    const dateTimeInLocalTime = moment.tz(data.date, timezone).local()
    const timeToDisplay = ReadableDifference(dateTimeInLocalTime)

    return (
      <div className=' bg-1 p-4'>
        <Vertical>
          <Horizontal className='pb-1'>
            <Texto appearance='medium' weight='bold' className='mr-4'>
              Date:
            </Texto>
            <Texto align='right' className='flex-1' weight='bold'>
              {dateTime} {timezone}
            </Texto>
          </Horizontal>

          <Horizontal className='pb-1'>
            <Texto appearance='medium' weight='bold' className='mr-4'>
              Time:
            </Texto>
            <Texto align='right' className='flex-1' weight='bold'>
              {timeToDisplay}
            </Texto>
          </Horizontal>
          <Horizontal className='pb-1'>
            <Texto appearance='medium' weight='bold' className='mr-4'>
              Volume:
            </Texto>
            <Texto align='right' className='flex-1' weight='bold'>
              {addCommasToNumber(volume, 0)}
            </Texto>
          </Horizontal>
          <Horizontal className='pb-1'>
            <Texto appearance='medium' weight='bold' className='mr-4'>
              Ullage:
            </Texto>
            <Texto align='right' className='flex-1' weight='bold'>
              {addCommasToNumber(maxPhysicalVolume - volume, 0)}
            </Texto>
          </Horizontal>
          {rundownToggled && <TooltipOrderDisplay data={data} />}
        </Vertical>
      </div>
    )
  }

  return null
}

function TooltipOrderDisplay({ data }) {
  if (!data?.orders) return null
  return data?.orders?.map((order) => (
    <Vertical className='pt-2 mt-1' style={{ borderTop: 'solid 1px var(--gray-300)' }}>
      <Horizontal>
        <Texto appearance='medium' weight='bold' className='mr-4'>
          Order Number:
        </Texto>
        <Texto align='right' className='flex-1' weight='bold'>
          {order.order_number}
        </Texto>
      </Horizontal>
      <Horizontal>
        <Texto appearance='medium' weight='bold' className='mr-4'>
          Order Volume:
        </Texto>
        <Texto align='right' className='flex-1' weight='bold'>
          {addCommasToNumber(order.volume)}
        </Texto>
      </Horizontal>
    </Vertical>
  ))
}
